// import { AdvancedEnemyAI } from "@/core/battle/ai/AIEnemyAdvanced";
// import { AIStrategicManager } from "@/core/battle/ai/strategic/AIStrategicManager";
import { AITurnManager } from "@/core/battle/ai/AITurnManager";
import { AIPlan } from "@/core/battle/ai/types/ai.types";
import { AnimationController } from "@/core/battle/AnimationController";
import { CombatExecutor } from "@/core/battle/CombatExecutor";
import { ControlStateMachine } from "@/core/battle/ControlStateMachine";
import { EntityRegistry } from "@/core/battle/EntityRegistry";
import { HighlightManager } from "@/core/battle/HighlightManager";
import { InputController } from "@/core/battle/InputController";
import { MovementExecutor } from "@/core/battle/MovementExecutor";
import { PathCalculator } from "@/core/battle/PathCalculator";
import { SkillManager } from "@/core/battle/SkillManager";
import { TurnManager } from "@/core/battle/TurnManager";
import { ControlState, BattleStatus, FactionType, SkillExecutedEvent, TerrainType } from "@/core/types/battle.types";
import { smoothPath } from "@/core/utils/battle.utils";
import { coordsToId } from "@/core/utils/hex.utils";
import { BattleBus } from "@/events/Events";
import { getActivePlayerUnit, getActivePool } from "@/store/battleSelectors";
import { useBattleStore } from "@/store/useBattleStore";
import { BattleCameraManager } from "@/view/BattleCameraManager";
import { BattleHex } from "@/view/renderers/BattleHex";
import { PathRenderer } from "@/view/renderers/PathRenderer";
import { gsap } from "gsap";

import { shallow } from 'zustand/shallow';



const PATH_SMOOTHING_FACTOR = 0.3;



export class BattleManager {
    private unsubscribers: (() => void)[] = [];
    private aiTaskQueue: Promise<void> = Promise.resolve();

    constructor(
        private pathRenderer: PathRenderer, 
        private inputController: InputController,
        private skillManager: SkillManager,
        private cameraManager: BattleCameraManager,
        private highlightManager: HighlightManager,
        private entityRegistry: EntityRegistry,
        private stateMachine: ControlStateMachine,
        private turnManager: TurnManager,
        private aiTurnManager: AITurnManager,
    ) {
        this.setupStoreSubscriptions();
    }

    // --- STORE SUBSCRIPTIONS ---
    
    private setupStoreSubscriptions(): void {
        this.unsubscribers.push(
            useBattleStore.subscribe(state => state.targetHexes, () => this.skillRangeHexHighlights()),
            useBattleStore.subscribe(state => state.reachableHexes, () => this.reachableHexesHighlight()),
            useBattleStore.subscribe(state => state.zocHexes, () => this.reachableHexesHighlight()),
            useBattleStore.subscribe(state => state.selectedHexId, hexId => this.handleHexSelection(hexId)),
            useBattleStore.subscribe(state => state.inspectedUnitId, () => this.reachableHexesHighlight()),
            // useBattleStore.subscribe(
            //     state => [state.selectedUnitId, state.turnQueue[0]] as const, // Чітко стежимо за парою
            //     ([unitId, _currentPool]) => {
            //         console.log('selectedUnitId', unitId, _currentPool);
            //         if (!unitId) return;
                        
            //         this.handleUnitSelection(unitId);
            //     },
            //     { equalityFn: shallow }
            // ),
            useBattleStore.subscribe(
                state => state.selectedUnitId,
                (unitId, prevUnitId) => {
                    if (unitId === prevUnitId) return;
                    
                    
                    this.handleUnitSelection(unitId);
                }
            ),

            // Update input state based on skill selection
            // useBattleStore.subscribe(state => state.selectedSkillId, skillId => {
            //     if (skillId && this.stateMachine.isAnyOf(ControlState.Idle)) {
            //         this.stateMachine.setState(ControlState.SkillTargeting);
            //     } else if (!skillId && this.stateMachine.isAnyOf(ControlState.SkillTargeting)) {
            //         this.stateMachine.setState(ControlState.Idle);
            //     }
            // }),

            // useBattleStore.subscribe(
            //     (state) => {
            //         const unit = state.selectedUnitId ? state.units.get(state.selectedUnitId) : null;
            //         return unit ? { id: unit.unitId, stamina: unit.currentStamina } : null;
            //     },
            //     (current, previous) => {
            //         // Если юнита нет или стамина не изменилась — ничего не делаем
            //         if (!current || (previous && current.stamina === previous.stamina && current.id === previous.id)) {
            //             return;
            //         }

            //         // МАГИЯ ЗДЕСЬ:
            //         // Стамина изменилась -> Проверяем, наш ли это юнит -> Обновляем UI
            //         this.handleStaminaChange(current.id);
            //     },
            //     { equalityFn: shallow } // Важно для оптимизации
            // )
        );

        this.stateMachine.onStateChange((from, to) => {
            // 1. ПРИБИРАННЯ: Якщо ми переходимо в "блокуючий" стан (анімація руху або атаки)
            if (this.stateMachine.isBlocking()) {
                this.highlightManager.clearHoverState();
                this.highlightManager.resetAllHexes();
                // this.pathRenderer.clear();
                // Можна також додати очистку шляху PathRenderer
            }

            // 2. ПОВЕРНЕННЯ: Якщо ми повернулися в режим очікування або вибору цілі
            if (to === ControlState.Idle || to === ControlState.SkillTargeting) {
                // Якщо в Zustand все ще вибрано скіл (багаторазове використання)
                // метод автоматично оновить сітку радіусу
                this.highlightManager.updateSkillRangeHighlights();
                useBattleStore.getState().resetSelectedPath();
                useBattleStore.getState().setSelectedHex(null);
                // Відновлюємо ховер на основі поточної позиції миші
                // (Використовуємо координати, які InputController трекає)
                // const mouse = this.inputController.getLastMousePosition();
                // if (mouse && useBattleStore.getState().status === BattleStatus.Playing) {
                //     this.highlightManager.restoreHoverHighlightsAfterAnimation(mouse.x, mouse.y);
                // }
            }

            if (from === ControlState.PlayerMoving && to === ControlState.Idle) {
                // const unit = getActivePlayerUnit(useBattleStore.getState());
                const unitId = useBattleStore.getState().selectedUnitId
                
                if (unitId) this.handleStaminaChange(unitId);
            }

            if (from === ControlState.PlayerAttacking && to === ControlState.Idle) {
                // const unit = getActivePlayerUnit(useBattleStore.getState());
                const unitId = useBattleStore.getState().selectedUnitId;
                const skillId = useBattleStore.getState().selectedSkillId;
                if (skillId && unitId) this.checkSkillAffordability(unitId, skillId);
                
                if (unitId) this.handleStaminaChange(unitId);
            }
        });

        // BattleBus.on('SKILL_EXECUTED', (event: SkillExecutedEvent) => {
        //     const unitVisual = this.entityRegistry.getUnit(event.targetId);
        //     if (unitVisual) {
        //         this.cameraManager.focusOnPoint({ x: unitVisual.x, y: unitVisual.y });

        //     }
        // });
    }

    private checkSkillAffordability(unitId: string, skillId: string) {
        const unit = useBattleStore.getState().units.get(unitId);
        if (!unit) return;
        const skill = unit.skills.find(s => s.id === skillId) || null;
        if (!skill) return;

        if (unit.currentStamina < skill.cost) {
            // Clear skill selection if not enough stamina for a second use
            this.skillManager.deselectSkill();
        } else {
            this.skillManager.selectSkill(skillId);
        }
    }

    private handleUnitSelection(unitId: string | null): void {

        this.resetSelection();
        if (!unitId) {
            useBattleStore.getState().setReachableHexes([], new Map<string, number>());
            return;
        }
        const gameStatus = useBattleStore.getState().status;
        if (gameStatus !== BattleStatus.Playing) {
            useBattleStore.getState().setSelectedUnit(null);
            return;
        }
        const state = useBattleStore.getState()
        const unitData = state.units.get(unitId);
        if (unitData && unitData.faction === FactionType.Player) {
            const resPath = PathCalculator.getReachableNodes(
                unitData, 
                { currentStamina: unitData.currentStamina, speed: unitData.stats.speed },
                state.battleMap
            )
            state.setReachableHexes(resPath.reachableIds, resPath.reachableMap);
        }

        const unitVisual = this.entityRegistry.getUnit(unitId);
        if (unitVisual) {
            this.cameraManager.focusOnPoint({ x: unitVisual.x, y: unitVisual.y });
            unitVisual.startIdle();
            unitVisual.startBadgeHighlight();
        }

        const activePool = getActivePool(useBattleStore.getState());
        if (activePool) {
            if (activePool.faction === FactionType.Enemy) {
                this.stateMachine.setState(ControlState.EnemyTurn);
                this.handleEnemyTurn();
                // this.turnManager.endUnitTurn(unitId);
            } else {
                this.stateMachine.setState(ControlState.Idle);
            }
        }
    }

    private handleStaminaChange(unitId: string) {
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId);

        if (!unit || unit.faction !== FactionType.Player) return;
        
        if (!this.turnManager.canUnitStillAct(unitId)) {
            this.turnManager.endUnitTurn(unitId);
        } else {
            // 1. Пересчитываем ходьбу
            const resPath = PathCalculator.getReachableNodes(
                unit, 
                { currentStamina: unit.currentStamina, speed: unit.stats.speed },
                state.battleMap
            );
            state.setReachableHexes(resPath.reachableIds, resPath.reachableMap);
            useBattleStore.getState().setZocHexes(new Set<string>());

            // 2. Пересчитываем скиллы (если выбран)
            if (state.selectedSkillId) {
                this.highlightManager.updateSkillRangeHighlights();
            }
        }
        
    }

    private skillRangeHexHighlights() {
        const { units, selectedUnitId } = useBattleStore.getState();
        const isPlayer = selectedUnitId ? units.get(selectedUnitId)?.faction === FactionType.Player : null;
        if (isPlayer) {
            this.highlightManager.updateSkillRangeHighlights();
        }
        
    }

    private reachableHexesHighlight() {
        const { units, selectedUnitId } = useBattleStore.getState();
        const isPlayer = selectedUnitId ? units.get(selectedUnitId)?.faction === FactionType.Player : null;
        if (isPlayer) {
            this.highlightManager.updateReachableHighlights();   
        }
    }
    

    private handleHexSelection(hexId: string | null) {
        // click on unit or obstacle
        if (!hexId) {
            this.pathRenderer.clear();
            return;
        };
        
        const state = useBattleStore.getState();
        if (!state.selectedUnitId) return;
        const unit = state.units.get(state.selectedUnitId);
        if (!unit) return;

        const startHexId = coordsToId(unit);
        const resPath = PathCalculator.getPathToHex(startHexId, hexId, state.reachableHexesWithCost, state.battleMap);
        useBattleStore.getState().setSelectedPath(resPath);
        this.showUnitPath(unit.unitId, resPath.path);
    }
    
    private resetSelection(): void { 
        const inputState = this.stateMachine.getState();
        // console.log("selectedUnitId resetSelection", inputState);
        if (inputState === ControlState.Idle || inputState === ControlState.EnemyTurn) { 
            useBattleStore.getState().setSelectedHex(null);
            useBattleStore.getState().resetSelectedPath();
            useBattleStore.getState().setSelectedSkill(null);
            PathCalculator.clearCache();
            this.pathRenderer.clear();
            useBattleStore.getState().setZocHexes(new Set<string>());
            this.highlightManager.resetAllHexes();
            this.highlightManager.resetAllUnits();
            this.highlightManager.clearHoverState();
        } 
    }

    private showUnitPath(unitId: string, path: string[]): void {
        const units = useBattleStore.getState().units;
        const unit = units.get(unitId);
        const unitVisual = this.entityRegistry.getUnit(unitId);
        if (!unitVisual || !unit) return;
        const fullPath = PathCalculator.constructFullPath(unitVisual, path, this.entityRegistry.getAllHexes());
        const lastPoint = path.at(-1);
        
        const hex = this.entityRegistry.getHex(lastPoint!);
        const isHill = hex?.data.terrain === TerrainType.Hill;

        const enemies = [...units.values()].filter(u => u.faction !== unit.faction);
        const zocHexes = PathCalculator.getPathZocHexes(unit, path, enemies);
        
        const smoothedPath = smoothPath(fullPath, isHill);
        const spline = this.pathRenderer.getSplinePoints(smoothedPath);
        this.pathRenderer.drawPathFromPoints(spline, zocHexes.size > 0);
        useBattleStore.getState().setZocHexes(zocHexes);
    }
    

    /**
     * Handle the entire enemy turn phase
     * Simplified - delegates to AITurnManager
     */
    private async handleEnemyTurn(): Promise<void> {
        // Отримуємо поточний ID на момент виклику підписки
        const state = useBattleStore.getState();
        const targetUnitId = state.selectedUnitId;

        if (!targetUnitId) return;

        // Нанизуємо нову задачу на ланцюжок
        this.aiTaskQueue = this.aiTaskQueue.then(async () => {
            // ВАЖЛИВО: Коли черга дійшла до цього юніта, 
            // перевіряємо стан світу ЗНОВУ, бо він міг змінитися, поки ми чекали
            const currentState = useBattleStore.getState();
            const currentPool = getActivePool(currentState);
            const unit = currentState.units.get(targetUnitId);

            // Перевіряємо, чи цей юніт все ще актуальний для ходу
            if (
                !unit || 
                unit.isDead || 
                unit.faction !== FactionType.Enemy ||
                !currentPool || 
                currentPool.faction !== FactionType.Enemy ||
                currentState.finishedPools.some(pool => pool.unitIds.includes(targetUnitId))
            ) {
                return; 
            }

            console.log('[EnemyTurn] Processing in queue:', targetUnitId);

            try {
                // Встановлюємо стан блокування, щоб гравець нічого не натиснув
                if (!this.stateMachine.is(ControlState.EnemyTurn)) {
                    this.stateMachine.setState(ControlState.EnemyTurn);
                }

                await this.aiTurnManager.executeUnitTurn(targetUnitId);
                
            } catch (error) {
                console.error(`[EnemyTurn] Critical AI error for ${targetUnitId}:`, error);
            } finally {
                console.log('[EnemyTurn] Task finished for:', targetUnitId);
                // Тут можна додати невелику паузу між юнітами для візуального комфорту
                // await new Promise(resolve => setTimeout(resolve, 300));
            }
        });

        // Ми повертаємо цей ланцюжок, щоб викликаючий код міг його await-ити за потреби
        return this.aiTaskQueue;
    }
    


    // --- ФІНАЛЬНИЙ ДЕСТРУКТОР ---
    public destroy(): void {
        console.warn("BattleManager: Знищення...");

        // 1. Відписка від Zustand (найважливіше для RAM)
        this.unsubscribers.forEach(unsub => unsub());
        this.unsubscribers = [];

        // 3. Зупинка GSAP (щоб не анімувати видалені об'єкти)
        gsap.killTweensOf(this);
        
        // 6. Очищення рендерера шляху
        this.pathRenderer.clear();

        console.log("BattleManager: Пам'ять чиста.");
    }

}